import {useEffect, useMemo} from 'react';
import {ActionIcon, Alert, Badge, Button, Checkbox, Group, Stack, Text, Tooltip} from '@mantine/core';
import {Field, FieldArray, useFormikContext} from 'formik';
import {
    FormikNumberInput,
    FormikSelect,
    FormikSwitch,
    FormikTextInput,
    PlatformFieldset,
} from '@flexinet/ui-components';
import {TbAlertTriangle, TbArrowDown, TbArrowUp, TbPlus, TbTrash} from 'react-icons/tb';
import {DeploymentAccount, PipelineStage, ProvisionPipelineInput} from '../types';
import {stage} from '../../../../config/stage';
import {
    LADDER_TIERS,
    LadderTier,
    STAGE_KIND_OPTIONS,
    availableTiers,
    findLadderStageIndex,
    isLadderStagePresent,
    ladderRungBlockedReason,
    ladderStageName,
    makeCustomStage,
    makeLadderStage,
    renumberStages,
    resolveDeployAccount,
    stageNeedsKind,
    stageTier,
    validateStages,
} from '../utils/stageLadder';

const STAGE_REGION_OPTIONS = [
    {value: 'us-west-2', label: 'us-west-2 (default)'},
    {value: 'us-east-1', label: 'us-east-1 (CloudFront certs)'},
];

const BUILD_PROVIDER_OPTIONS = [
    {value: 'CODEBUILD', label: 'CodeBuild'},
];

const DEPLOY_PROVIDER_OPTIONS = [
    {value: 'CLOUDFORMATION', label: 'CloudFormation'},
    {value: 'S3', label: 'S3 (static site sync)'},
];

// FNP-1017: the admin sees every stage and every option on it before provisioning.
// FNP-1346: the deploy target is now PICKED per stage and stored on it. It was
// derived from the stage name until the contract gained `deployTargetAccountId`,
// which is what made a tools-account deploy inexpressible.
// FNP-1024: `consoleStage` is injectable ONLY so tests can render every tier
// without rebuilding the bundle per stage. Production always uses the real
// build-time constant from config/stage.ts.
export const PipelineStagesEditor = ({consoleStage = stage}: {consoleStage?: string} = {}) => {
    const {values, setFieldValue} = useFormikContext<ProvisionPipelineInput>();
    // FNP-1346: memoised because the seeding effect below depends on both. `?? []`
    // builds a fresh array every render when the value is undefined, which would
    // re-fire the effect on each pass and, since the effect itself calls
    // setFieldValue, risk an update loop.
    const stages: PipelineStage[] = useMemo(() => values.stages ?? [], [values.stages]);
    const accounts: DeploymentAccount[] = useMemo(
        () => values.deploymentAccounts ?? [], [values.deploymentAccounts],
    );
    const tiersWithAccounts = availableTiers(accounts);
    const errors = validateStages(stages, accounts);

    // FNP-1017: the FieldArray helper performs the structural mutation (it keeps
    // Formik's touched/error metadata aligned with the moved indices, which a bare
    // setFieldValue does not). We then renumber `order` to match the new positions:
    // `order` is what the backend sorts by, so a stale value after a move would
    // reorder the generated pipeline in a way the admin never saw on screen.
    const renumberAfter = (next: PipelineStage[]) => {
        setFieldValue('stages', renumberStages(next));
    };

    // FNP-1346: seed `deployTargetAccountId` on any DEPLOY stage that has none but
    // whose name still resolves to an account.
    //
    // Records written before the field existed carry no target. Rendering the new
    // Select empty for those would show a blank required field on every legacy
    // pipeline, and an admin who saved without touching it would submit a stage the
    // API now REFUSES (FNP-1345 removed the fallback). Seeding turns the value the
    // old UI merely displayed into one that is actually stored and submitted.
    //
    // Only fills a blank -- it never overwrites a chosen target, and a stage whose
    // name resolves to nothing (a tools deploy, say) is deliberately left empty so
    // validateStages flags it rather than guessing.
    useEffect(() => {
        stages.forEach((stage, index) => {
            if (stage.stageKind !== 'DEPLOY' || stage.deployTargetAccountId) return;
            const inferred = resolveDeployAccount(stage, accounts);
            if (inferred) {
                setFieldValue(`stages.${index}.deployTargetAccountId`, inferred.accountId);
            }
        });
    }, [stages, accounts, setFieldValue]);

    return (
        <PlatformFieldset legend="Pipeline Stages">
            <Stack gap="md" my="xl">
                <Text size="sm" c="dimmed">
                    Every stage that will be built is listed here. Add, remove and reorder
                    them freely - what you see is exactly what gets provisioned. Stage kind
                    is required on every stage: an unclassified stage is dropped from the
                    generated pipeline without warning.
                </Text>

                <FieldArray name="stages">
                    {({push, remove, swap}) => (
                        <Stack gap="md">
                            {/* The full ladder, always visible. A rung with no matching
                                deployment account renders DISABLED, never hidden. */}
                            <PlatformFieldset legend="Deploy ladder">
                                <Stack gap="xs" my="md">
                                    <Text size="sm" c="dimmed">
                                        dev console: DeployDev &middot; test console: DeployDev then
                                        DeployTest &middot; prod console: DeployDev then DeployTest then
                                        DeployProd
                                    </Text>
                                    {LADDER_TIERS.map((tier: LadderTier) => {
                                        const present = isLadderStagePresent(stages, tier);
                                        // FNP-1024: TWO independent gates. The rung needs a
                                        // matching account AND must be at or below the tier
                                        // this console may build. Previously only the first
                                        // was checked, so a test console offered a clickable
                                        // DeployProd.
                                        const blockedReason = ladderRungBlockedReason(
                                            tier, tiersWithAccounts, consoleStage,
                                        );
                                        const selectable = !blockedReason;
                                        const label = ladderStageName(tier);
                                        return (
                                            <Group gap="xs" key={tier}>
                                                <Tooltip
                                                    disabled={selectable}
                                                    label={blockedReason ?? ''}
                                                >
                                                    <Checkbox
                                                        label={label}
                                                        checked={present}
                                                        disabled={!selectable}
                                                        aria-label={`${label} stage`}
                                                        onChange={(event) => {
                                                            if (event.currentTarget.checked) {
                                                                const added = makeLadderStage(
                                                                    tier, stages.length + 1,
                                                                );
                                                                push(added);
                                                                renumberAfter([...stages, added]);
                                                            } else {
                                                                // FNP-1149: find the rung by TIER. Looking it
                                                                // up by `label` (the ladder's spelling) found
                                                                // nothing when the stage came from the analyzer
                                                                // as `test`/`prod`, so unticking removed nothing.
                                                                const idx = findLadderStageIndex(stages, tier);
                                                                if (idx >= 0) {
                                                                    remove(idx);
                                                                    renumberAfter(
                                                                        stages.filter((_, i) => i !== idx),
                                                                    );
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Tooltip>
                                                {!selectable && (
                                                    <Badge color="gray" variant="light" size="sm">
                                                        no {tier.toLowerCase()} account
                                                    </Badge>
                                                )}
                                            </Group>
                                        );
                                    })}
                                </Stack>
                            </PlatformFieldset>

                            {errors.length > 0 && (
                                <Alert
                                    color="red"
                                    icon={<TbAlertTriangle/>}
                                    title="Stages are not ready to provision"
                                >
                                    <Stack gap={2}>
                                        {errors.map((e, i) => (
                                            <Text key={i} size="sm">
                                                {e.name ? `${e.name}: ` : ''}{e.message}
                                            </Text>
                                        ))}
                                    </Stack>
                                </Alert>
                            )}

                            {stages.map((stage, index) => {
                                const isDeploy = stage.stageKind === 'DEPLOY';
                                // FNP-1017: resolve the target the way the BACKEND will
                                // (by stage-name tier), not by the exact ladder spelling.
                                // ladderTierOfStage only matched the literal 'DeployProd'
                                // spelling, so a renamed prod stage ('Deploy Production',
                                // 'DeployProdUsEast') resolved to no account and the
                                // FNP-709 warning below silently stopped rendering — while
                                // the backend still classified it as prod and still refused.
                                const resolvedAccount = resolveDeployAccount(stage, accounts);
                                const targetsProd = isDeploy && stageTier(stage) === 'PROD';
                                const stageLabel = stage.name || `Stage ${index + 1}`;

                                return (
                                    <PlatformFieldset key={index} legend={stageLabel}>
                                        <Stack gap="sm" my="md">
                                            <Group justify="flex-end" gap="xs">
                                                <ActionIcon
                                                    variant="subtle"
                                                    aria-label={`Move ${stageLabel} up`}
                                                    disabled={index === 0}
                                                    onClick={() => {
                                                        swap(index, index - 1);
                                                        const next = [...stages];
                                                        [next[index - 1], next[index]] =
                                                            [next[index], next[index - 1]];
                                                        renumberAfter(next);
                                                    }}
                                                >
                                                    <TbArrowUp/>
                                                </ActionIcon>
                                                <ActionIcon
                                                    variant="subtle"
                                                    aria-label={`Move ${stageLabel} down`}
                                                    disabled={index === stages.length - 1}
                                                    onClick={() => {
                                                        swap(index, index + 1);
                                                        const next = [...stages];
                                                        [next[index], next[index + 1]] =
                                                            [next[index + 1], next[index]];
                                                        renumberAfter(next);
                                                    }}
                                                >
                                                    <TbArrowDown/>
                                                </ActionIcon>
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="red"
                                                    aria-label={`Remove ${stageLabel}`}
                                                    onClick={() => {
                                                        remove(index);
                                                        renumberAfter(
                                                            stages.filter((_, i) => i !== index),
                                                        );
                                                    }}
                                                >
                                                    <TbTrash/>
                                                </ActionIcon>
                                            </Group>

                                            <Field
                                                name={`stages.${index}.name`}
                                                label="Stage Name"
                                                description="Shown as the CodePipeline stage name"
                                                component={FormikTextInput}
                                                withAsterisk
                                            />

                                            <Field
                                                name={`stages.${index}.order`}
                                                label="Order"
                                                description="Execution order - lower runs first"
                                                component={FormikNumberInput}
                                                min={1}
                                                withAsterisk
                                            />

                                            {/* stageKind is MANDATORY on every non-Source stage.
                                                No blank option is offered, deselect is disabled, and
                                                an unset value is flagged inline AND blocks submit. */}
                                            <Field
                                                name={`stages.${index}.stageKind`}
                                                label="Stage Kind"
                                                description="Required - determines which pipeline action is generated"
                                                component={FormikSelect}
                                                data={STAGE_KIND_OPTIONS}
                                                withAsterisk
                                                allowDeselect={false}
                                                error={stageNeedsKind(stage)
                                                    ? 'Required - an unclassified stage is silently dropped'
                                                    : undefined}
                                            />

                                            <Field
                                                name={`stages.${index}.buildProvider`}
                                                label="Build Provider"
                                                description="Build engine for this stage (optional)"
                                                component={FormikSelect}
                                                data={BUILD_PROVIDER_OPTIONS}
                                                clearable
                                            />

                                            {/* FNP-1267: buildCommand and artifactBaseDir came from the archetype
                                                defaults and were never shown. A pipeline therefore ran
                                                `npm run build:prod` against a repo with no such script, and wrote to
                                                `dist` while the repo built to `dev_build`. Both were invisible until
                                                the first run. Every value that shapes the pipeline is on this form. */}
                                            <Field
                                                name={`stages.${index}.buildCommand`}
                                                label="Build Command"
                                                description="The npm script this stage runs, e.g. npm run build:test"
                                                component={FormikTextInput}
                                            />

                                            <Field
                                                name={`stages.${index}.artifactBaseDir`}
                                                label="Artifact Base Directory"
                                                description="Directory the build writes to, relative to the repo root, e.g. dist"
                                                component={FormikTextInput}
                                            />

                                            <Field
                                                name={`stages.${index}.deployProvider`}
                                                label="Deploy Provider"
                                                description="How this stage applies its artifact (optional)"
                                                component={FormikSelect}
                                                data={DEPLOY_PROVIDER_OPTIONS}
                                                clearable
                                            />

                                            {isDeploy && (
                                                <>
                                                    {/* FNP-1017: the deploy target is DERIVED, not selected.
                                                        The contract has no per-stage account field — the backend
                                                        pairs a stage to an account by name suffix
                                                        (inferStageNameFromAccountName). A select here would be
                                                        dropped at submit while telling the admin it had been
                                                        honoured, so we SHOW the resolved account instead. To
                                                        change it, change the account group. */}
                                                    {/* FNP-1346: the target is PICKED, not derived. It used to be
                                                        read-only text showing whatever name inference produced,
                                                        which made a tools-account deploy impossible to express --
                                                        'flexi-tools' has no -dev/-test/-prod suffix, so nothing
                                                        ever resolved. Every account in the selected group is
                                                        offered, suffixed or not. */}
                                                    <Field
                                                        name={`stages.${index}.deployTargetAccountId`}
                                                        label="Deploy Target Account"
                                                        description="The account this stage deploys into. Required — provisioning refuses a deploy stage with no target."
                                                        component={FormikSelect}
                                                        withAsterisk
                                                        data={accounts.map((a) => ({
                                                            value: a.accountId,
                                                            label: a.accountName
                                                                ? `${a.accountName} (${a.accountId})`
                                                                : a.accountId,
                                                        }))}
                                                    />
                                                    {!stage.deployTargetAccountId && resolvedAccount && (
                                                        <Alert color="blue" icon={<TbAlertTriangle/>}>
                                                            This stage has no stored target. It currently resolves
                                                            to {resolvedAccount.accountName ?? resolvedAccount.accountId} by
                                                            matching its name, which is how records were written before
                                                            targets were explicit. Pick it above to store it.
                                                        </Alert>
                                                    )}
                                                    {!stage.deployTargetAccountId && !resolvedAccount && (
                                                        <Alert color="orange" icon={<TbAlertTriangle/>}>
                                                            No deploy target. Provisioning will be refused until one
                                                            is selected.
                                                        </Alert>
                                                    )}
                                                    <Field
                                                        name={`stages.${index}.stackName`}
                                                        label="CloudFormation Stack Name"
                                                        description="The stack this stage deploys; must match a template cdk synth emits (FNP-275)"
                                                        component={FormikTextInput}
                                                    />

                                                    <Field
                                                        name={`stages.${index}.region`}
                                                        label="Region"
                                                        description="Target region for this deploy stage"
                                                        component={FormikSelect}
                                                        data={STAGE_REGION_OPTIONS}
                                                    />
                                                </>
                                            )}

                                            <Field
                                                name={`stages.${index}.requiresApproval`}
                                                label="Requires manual approval"
                                                description={targetsProd
                                                    ? 'This stage deploys into a production account. Provisioning refuses an ungated prod deploy (FNP-709).'
                                                    : 'Insert a manual approval action ahead of this stage'}
                                                component={FormikSwitch}
                                            />

                                            {targetsProd && stage.requiresApproval !== true && (
                                                <Alert color="orange" icon={<TbAlertTriangle/>}>
                                                    This stage targets a production account with no approval
                                                    gate. Provisioning will reject it.
                                                </Alert>
                                            )}
                                        </Stack>
                                    </PlatformFieldset>
                                );
                            })}

                            <Group>
                                <Button
                                    variant="light"
                                    leftSection={<TbPlus/>}
                                    onClick={() => {
                                        const added = makeCustomStage(stages.length + 1);
                                        push(added);
                                        renumberAfter([...stages, added]);
                                    }}
                                >
                                    Add stage
                                </Button>
                            </Group>
                        </Stack>
                    )}
                </FieldArray>
            </Stack>
        </PlatformFieldset>
    );
};
